import { useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useSettingsStore } from '@/stores/settingsStore';
import { InlineWordEdit } from './InlineWordEdit';

interface AdminEditButtonProps {
  // Один из двух id обязателен. dictionaryWordId — приоритетнее (редактирование общего словаря).
  // Если только userWordId — пользовательское слово, редактируется свой UserWord + опция publish.
  dictionaryWordId?: number | null;
  userWordId?: number | null;
  // Колбэк после успешного сохранения — родитель решает, что делать с текущей карточкой
  // (для Input/SRS — обновить локально, для MC/Audition/Dictation — refetch через regenerate-card).
  onSaved?: (updatedWord: any) => void;
  // Inline = кнопка-чип внутри строки с POS/CEFR-плашками (не absolute).
  // Иначе — absolute в углу (position перекрывает дефолт top:6 right:6).
  inline?: boolean;
  position?: React.CSSProperties;
}

export function AdminEditButton({ dictionaryWordId, userWordId, onSaved, inline, position }: AdminEditButtonProps) {
  const user = useAuthStore((s) => s.user);
  const adminEditEnabled = useSettingsStore((s) => s.adminEditEnabled);
  const [open, setOpen] = useState(false);

  const isAdmin = !!user?.is_admin;
  // Нужен хотя бы один id (обычно есть userWordId, иногда плюс dictionaryWordId)
  const hasAnyId = dictionaryWordId != null || userWordId != null;
  if (!isAdmin || !adminEditEnabled || !hasAnyId) return null;

  const handleClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    if ('preventDefault' in e) e.preventDefault();
    setOpen(true);
  };

  return (
    <>
      <button
        onClick={handleClick}
        onTouchStart={(e) => e.stopPropagation()}
        title="Редактировать слово (админ)"
        aria-label="Редактировать слово"
        type="button"
        style={inline ? {
          // Inline-вариант: компактный чип в одной строке с POS/CEFR-плашками.
          // Размер совпадает с плашками (fontSize 11, padding 2/7).
          minHeight: 'unset',
          padding: '2px 6px',
          background: 'rgba(127,127,127,0.12)',
          border: '1px solid var(--theme-border)',
          borderRadius: 4,
          color: 'var(--theme-text-muted)',
          fontSize: 11,
          lineHeight: 1.3,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        } : {
          position: 'absolute',
          top: 6,
          right: 6,
          width: 28,
          height: 28,
          minHeight: 'unset',
          padding: 0,
          background: 'rgba(127,127,127,0.12)',
          border: '1px solid var(--theme-border)',
          borderRadius: 6,
          color: 'var(--theme-text-muted)',
          fontSize: 13,
          lineHeight: 1,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          zIndex: 10,
          ...(position ? { top: undefined, right: undefined, ...position } : null),
        }}
      >
        ✏️
      </button>

      {open && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 1000,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            overflowY: 'auto',
            padding: '20px 0',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--theme-bg)',
              borderRadius: 12,
              width: 'calc(100% - 32px)',
              maxWidth: 640,
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid var(--theme-border)',
            }}
          >
            <InlineWordEdit
              dictionaryWordId={dictionaryWordId ?? null}
              userWordId={userWordId ?? null}
              onClose={() => setOpen(false)}
              onSaved={onSaved}
            />
          </div>
        </div>
      )}
    </>
  );
}
